import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { InventoryFilters } from '@/lib/inventory/filters'

import type {
    Category,
    Product,
    Settings,
    StatusFilter
} from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
    ArrowLeftRight,
    FilterX,
    PackagePlus,
    PackageSearch,
    Search,
    X
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { pluralize } from '@/lib/format'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { useSettings } from '@/components/generals/settings-provider'
import { useInventoryActions } from '@/components/inventory/inventory-actions'
import PageHeader from '@/components/generals/page-header'
import SegmentedControl from '@/components/generals/segmented-control'
import EmptyState from '@/components/generals/empty-state'
import LoadError from '@/components/generals/load-error'
import ProductList from '@/components/inventory/product-list'
import { describeError } from '@/lib/db'
import { getCategories, getProducts, getSettings } from '@/lib/inventory/queries'

import {
    DEFAULT_FILTERS,
    UNCATEGORIZED,
    matchesCategory,
    matchesSearch,
    matchesStatus,
    readInventoryFilters,
    sortProducts,
    toInventoryQuery
} from '@/lib/inventory/filters'

import {
    DEFAULT_SETTINGS,
    PRODUCT_SORTS,
    STATUS_FILTERS
} from '@/constants/inventory'

type InventoryData = {
    products: Product[]
    categories: Category[]
}

interface InventoryPageProps {
    settings: Settings
    data: InventoryData | null
    initialFilters: InventoryFilters
}

interface InventoryViewProps {
    data: InventoryData
    initialFilters: InventoryFilters
}

const InventoryView: FC<InventoryViewProps> = ({ data, initialFilters }): ReactNode => {
    const router = useRouter()
    const { money, number } = useSettings()
    const { openNewProduct } = useInventoryActions()
    const [filters, setFilters] = useState<InventoryFilters>(initialFilters)
    const isFirstRender = useRef(true)
    const filtersKey = JSON.stringify(initialFilters)
    const appliedFiltersKey = useRef(filtersKey)

    const { products, categories } = data

    useEffect(() => {
        if (appliedFiltersKey.current === filtersKey) return

        appliedFiltersKey.current = filtersKey
        setFilters(initialFilters)
    }, [filtersKey])

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false

            return
        }

        const timer = setTimeout(() => {
            void router.replace({ pathname: '/inventario', query: toInventoryQuery(filters) }, undefined, { shallow: true, scroll: false })
        }, 350)

        return () => clearTimeout(timer)
    }, [filters])

    const scoped = useMemo(() => {
        return products.filter(product => matchesSearch(product, filters.search) && matchesCategory(product, filters.category))
    }, [products, filters.search, filters.category])

    const counts = useMemo(() => {
        const result: Record<StatusFilter, number> = { todos: scoped.length, reponer: 0, agotados: 0, disponibles: 0 }

        for (const product of scoped) {
            if (matchesStatus(product, 'reponer')) result.reponer++
            if (matchesStatus(product, 'agotados')) result.agotados++
            if (matchesStatus(product, 'disponibles')) result.disponibles++
        }

        return result
    }, [scoped])

    const visible = useMemo(() => {
        return sortProducts(scoped.filter(product => matchesStatus(product, filters.status)), filters.sort)
    }, [scoped, filters.status, filters.sort])

    const visibleValue = visible.reduce((total, product) => total + product.quantity * product.cost, 0)
    const visibleUnits = visible.reduce((total, product) => total + product.quantity, 0)
    const hasFilters = Boolean(filters.search.trim() || filters.category || filters.status !== 'todos')
    const hasUncategorized = products.some(product => !product.categoryId)

    const update = (patch: Partial<InventoryFilters>) => setFilters(previous => ({ ...previous, ...patch }))
    const clearFilters = () => setFilters(previous => ({ ...DEFAULT_FILTERS, sort: previous.sort }))

    if (!products.length) {
        return (
            <div className='rounded-xl border border-dashed bg-card'>
                <EmptyState
                    icon={PackagePlus}
                    title='Todavía no hay productos'
                    description='Añade tu primer producto con su stock actual y el mínimo que quieres tener. A partir de ahí, cada entrada y salida queda registrada.'
                    action={(
                        <Button onClick={openNewProduct}>
                            <PackagePlus aria-hidden='true' />
                            Nuevo producto
                        </Button>
                    )}
                    className='py-16'
                />
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-3 rounded-xl border bg-card p-3 shadow-xs sm:p-4'>
                <div className='flex flex-col gap-3 lg:flex-row'>
                    <div className='relative flex-1'>
                        <label htmlFor='inventory-search' className='sr-only'>
                            Buscar productos
                        </label>

                        <Search className='pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />

                        <Input
                            id='inventory-search'
                            type='search'
                            value={filters.search}
                            onChange={event => update({ search: event.target.value })}
                            placeholder='Buscar por nombre, código, proveedor o ubicación'
                            autoComplete='off'
                            className='pr-9 pl-9 [&::-webkit-search-cancel-button]:hidden'
                        />

                        {filters.search && (
                            <button
                                type='button'
                                onClick={() => update({ search: '' })}
                                className='absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
                                aria-label='Borrar búsqueda'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        )}
                    </div>

                    <div className='grid grid-cols-2 gap-3 lg:flex lg:w-auto'>
                        <div>
                            <label htmlFor='inventory-category' className='sr-only'>
                                Filtrar por categoría
                            </label>

                            <NativeSelect
                                id='inventory-category'
                                value={filters.category}
                                onChange={event => update({ category: event.target.value })}
                                className='w-full lg:w-48'
                            >
                                <NativeSelectOption value=''>
                                    Todas las categorías
                                </NativeSelectOption>

                                {categories.map(category => (
                                    <NativeSelectOption key={category.id} value={category.id}>
                                        {category.name}
                                    </NativeSelectOption>
                                ))}

                                {hasUncategorized && (
                                    <NativeSelectOption value={UNCATEGORIZED}>
                                        Sin categoría
                                    </NativeSelectOption>
                                )}
                            </NativeSelect>
                        </div>

                        <div>
                            <label htmlFor='inventory-sort' className='sr-only'>
                                Ordenar productos
                            </label>

                            <NativeSelect
                                id='inventory-sort'
                                value={filters.sort}
                                onChange={event => update({ sort: event.target.value as InventoryFilters['sort'] })}
                                className='w-full lg:w-56'
                            >
                                {PRODUCT_SORTS.map(option => (
                                    <NativeSelectOption key={option.value} value={option.value}>
                                        {option.label}
                                    </NativeSelectOption>
                                ))}
                            </NativeSelect>
                        </div>
                    </div>
                </div>

                <SegmentedControl
                    label='Filtrar por estado del stock'
                    value={filters.status}
                    options={STATUS_FILTERS.map(option => ({ ...option, count: counts[option.value] }))}
                    onChange={status => update({ status })}
                    className='self-start'
                />
            </div>

            <div className='flex flex-wrap items-center justify-between gap-2 px-1'>
                <p className='text-sm text-muted-foreground' aria-live='polite'>
                    <span className='font-medium text-foreground tabular-nums'>{number(visible.length)}</span>
                    {' '}
                    {pluralize(visible.length, 'producto', 'productos')}
                    {visible.length !== products.length && <> de {number(products.length)}</>}
                    <span className='mx-2 text-border' aria-hidden='true'>|</span>
                    <span className='tabular-nums'>{number(visibleUnits)}</span> {pluralize(visibleUnits, 'unidad', 'unidades')}
                    <span className='mx-2 text-border' aria-hidden='true'>|</span>
                    Valor <span className='font-medium text-foreground tabular-nums'>{money(visibleValue)}</span>
                </p>

                {hasFilters && (
                    <Button variant='ghost' size='sm' onClick={clearFilters}>
                        <FilterX aria-hidden='true' />
                        Quitar filtros
                    </Button>
                )}
            </div>

            {visible.length ? (
                <ProductList products={visible} />
            ) : (
                <div className='rounded-xl border border-dashed bg-card'>
                    <EmptyState
                        icon={PackageSearch}
                        title={filters.status === 'reponer' && !filters.search && !filters.category ? 'Nada que reponer' : 'Ningún producto coincide'}
                        description={filters.status === 'reponer' && !filters.search && !filters.category
                            ? 'Todos tus productos están por encima de su stock mínimo.'
                            : 'Prueba con otra búsqueda o quita los filtros para ver todo el inventario.'}
                        tone={filters.status === 'reponer' && !filters.search && !filters.category ? 'success' : 'default'}
                        action={hasFilters ? (
                            <Button variant='outline' onClick={clearFilters}>
                                <FilterX aria-hidden='true' />
                                Quitar filtros
                            </Button>
                        ) : undefined}
                    />
                </div>
            )}
        </div>
    )
}

const InventoryPage: FC<InventoryPageProps> = ({ data, initialFilters }): ReactNode => {
    const { openMovement, openNewProduct } = useInventoryActions()

    return (
        <Layout>
            <Seo
                title='Inventario'
                description='Consulta el stock de cada producto, filtra por categoría o estado y registra entradas y salidas.'
            />

            <div className='flex flex-col gap-6'>
                <PageHeader
                    title='Inventario'
                    description='Todos tus productos con su stock, su estado y su valor. Busca, filtra y registra movimientos desde aquí.'
                    actions={(
                        <>
                            <Button variant='outline' onClick={() => openMovement()}>
                                <ArrowLeftRight aria-hidden='true' />
                                Registrar movimiento
                            </Button>

                            <Button onClick={openNewProduct}>
                                <PackagePlus aria-hidden='true' />
                                Nuevo producto
                            </Button>
                        </>
                    )}
                />

                {data ? <InventoryView data={data} initialFilters={initialFilters} /> : <LoadError />}
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<InventoryPageProps> = async ({ query }) => {
    const initialFilters = readInventoryFilters(query)

    try {
        const [settings, products, categories] = await Promise.all([getSettings(), getProducts(), getCategories()])

        return { props: { settings, data: { products, categories }, initialFilters } }
    }
    catch (error) {
        console.error('[inventario] No se pudo cargar el inventario:', describeError(error))

        return { props: { settings: DEFAULT_SETTINGS, data: null, initialFilters } }
    }
}

export default InventoryPage
